import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Qué módulos tiene contratados la empresa.
 *
 * El superadministrador vende planes con unos módulos y no otros —«yo no le
 * voy a dar a todo el mundo opción de que tenga acceso al hotel»—, pero el
 * menú listaba las 17 rutas fijas para todos. El plan era decorativo: una
 * peluquería con plan Gratis veía Hotel, Daycare y Membresías y los usaba.
 *
 * `my_modules()` resuelve el plan del tenant más lo que se le haya añadido o
 * quitado a mano, y ya existía sin que nadie la llamara.
 *
 * Esto NO es seguridad: ocultar una entrada del menú no impide escribir la
 * ruta. Lo que protege de verdad son las políticas RLS de cada tabla. Aquí se
 * trata de no ofrecer lo que no se ha vendido.
 */
export function useModulos() {
  const tenantId = useAuthStore((s) => s.tenant?.id);
  const esSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin) ?? false;

  const q = useQuery({
    queryKey: ['modulos', tenantId],
    enabled: Boolean(tenantId) && !esSuperAdmin,
    // Cambia cuando el superadministrador toca el plan, que es poco. Una hora
    // evita pedirlo en cada pantalla sin dejar el menú desactualizado un día.
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.rpc('my_modules');
      if (error) throw error;
      return (data ?? []) as string[];
    },
  });

  /**
   * Si algo falla —sin red, la consulta no responde— se deja pasar TODO.
   *
   * Es deliberado: un fallo de red no es «no tienes ese módulo». Esconder el
   * hotel porque la consulta no respondió deja a la peluquería sin poder
   * trabajar y sin entender por qué. Lo peor que puede pasar dejando pasar es
   * que alguien abra una pantalla que no contrató y las políticas RLS no le
   * devuelvan nada.
   */
  const tiene = (modulo?: string | string[]): boolean => {
    if (!modulo || (Array.isArray(modulo) && modulo.length === 0)) return true; // sin módulo: siempre
    if (esSuperAdmin) return true;     // el dueño de la plataforma lo ve todo
    if (q.isLoading || q.isError || !q.data) return true;
    // Varios = basta con uno: «Servicios» sirve a la peluquería y a VetCare.
    return (Array.isArray(modulo) ? modulo : [modulo]).some((m) => q.data!.includes(m));
  };

  /**
   * La versión estricta, para los módulos que se venden aparte (facturación
   * electrónica): mientras carga o si falla, NO.
   *
   * La regla de arriba —ante la duda, dejar pasar— está pensada para no
   * esconder el hotel a quien lo pagó. Con un módulo de pago extra el riesgo
   * es el contrario: que un salón que no lo tiene vea aparecer una entrada
   * que no contrató cada vez que la red parpadea.
   */
  const tieneConfirmado = (modulo: string): boolean => {
    if (esSuperAdmin) return true;
    return q.data?.includes(modulo) ?? false;
  };

  return { modulos: q.data ?? [], tiene, tieneConfirmado, cargando: q.isLoading };
}

/**
 * Cómo se llama quien atiende la cita.
 *
 * «Groomer» es la palabra de una peluquería, y en una clínica la cita la
 * atiende la veterinaria. Una peluquería sin clínica sigue viendo «Groomer»
 * tal cual; donde hay clínica se dice «Atiende», que vale para las dos.
 */
export function useQuienAtiende(): string {
  const { tieneConfirmado } = useModulos();
  return tieneConfirmado('vetcare') ? 'Atiende' : 'Groomer';
}
